import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import isBetween from 'dayjs/plugin/isBetween';
import 'dayjs/locale/th';
dayjs.extend(relativeTime);
dayjs.extend(isBetween);
dayjs.locale('th');

export const getCurrentEducationYear = () => {
  const currentYear = dayjs().year();
  const startSemesterDate = dayjs(new Date(currentYear, 4, 16));
  const today = dayjs();

  const yearString = today.isAfter(startSemesterDate) ? currentYear : currentYear - 1;
  return Number.parseInt(yearString);
};

const DMY = 'D MMM YY';
const DM = 'D MMM';
const D = 'D';
const DMYHHMM = 'D MMM YY HH:mm';
const HHMM = 'HH:mm';

export const getThaiDisplayDate = (date, format) => {
  if (date && dayjs(date).isValid()) {
    return dayjs(date)
      .add(543, 'y')
      .format(format || DMY);
  }
  return date;
};

export const getTimeFromNow = date => {
  const mDate = dayjs(date);
  if (mDate.isValid() && mDate.isBefore()) {
    return mDate.fromNow();
  }
  return date;
};

export const getDiff = (date, unit = 'day') => {
  const mDate = dayjs(date);
  if (mDate.isValid()) {
    return mDate.diff(dayjs(), unit);
  }
  return date;
};

export const isInBetween = (start, end, unit = 'day', format = '[]') => {
  return (
    start &&
    dayjs(start).isValid() &&
    end &&
    dayjs(end).isValid() &&
    dayjs().isBetween(start, end, unit, format)
  );
};

export const getDayOfWeekTH = date => {
  return `วัน${dayjs(date).format('ddd')}`;
};

export const getRemainingDays = (start = null, end = null, showDaysInNumber = false) => {
  let remainingDays;
  const startDate = dayjs(start);
  const endDate = dayjs(end);
  const today = dayjs();

  if (startDate.isValid()) {
    remainingDays = startDate.fromNow();

    const startDiffToday = startDate.diff(today, 'day');
    // console.log('startdiff', startDiffToday);
    if (showDaysInNumber) {
      remainingDays = today.diff(startDate, 'days');
    } else {
      if (startDiffToday < 29) {
        if (startDiffToday === 0) {
          remainingDays = 'พรุ่งนี้';
        } else {
          remainingDays = `อีก ${startDiffToday + 1} วัน`;
        }
      }
    }

    if (endDate.isValid()) {
      if (showDaysInNumber) {
        remainingDays = endDate.diff(startDate, 'day');
      } else {
        if (today.isBetween(startDate, endDate, 'day', '[]')) {
          const diffStart = endDate.diff(startDate, 'day');
          const diffToday = endDate.diff(today, 'day');
          // console.log('diff', diffStart, diffToday);
          remainingDays = `อีก ${diffToday + 1} วัน`;
          if (today.isSame(endDate, 'day')) {
            if (diffStart >= 1) {
              remainingDays = 'วันสุดท้าย';
            } else {
              remainingDays = 'วันนี้';
            }
          }
        }
      }
    }
  }

  return remainingDays;
};

export const getExamDuration = (start, end) => {
  if (
    start &&
    dayjs(start).isValid() &&
    end &&
    dayjs(end).isValid() &&
    dayjs(start).isBefore(end)
  ) {
    const mStart = dayjs(start);
    const mEnd = dayjs(end);

    const min = mEnd.diff(mStart, 'minutes');
    if (min <= 180) return { time: min, unit: 'นาที' };

    const hour = +mEnd.diff(mStart, 'hours', true).toFixed(1);
    if (hour <= 6) return { time: hour, unit: 'ชั่วโมง' };

    const day = +mEnd.diff(mStart, 'days', true).toFixed(1);

    return { time: day, unit: 'วัน' };
  }
  return undefined;
};

export const generateDateRange = (start, end, options) => {
  if (!start || !end) return '';

  const mStart = dayjs(start);
  const mEnd = dayjs(end);

  if (!mStart.isValid() || !mEnd.isValid() || mStart.isAfter(mEnd)) return '';

  return genFormat(mStart, mEnd, testRoute);
};

export const generateTimeRange = (start, end, options) => {
  if (!start || !end) return '';

  const mStart = dayjs(start);
  const mEnd = dayjs(end);

  if (!mStart.isValid() || !mEnd.isValid() || mStart.isSameOrAfter(mEnd)) return '';
  return `${getThaiDisplayDate(mStart, HHMM)}${hyphen(true)}${getThaiDisplayDate(mEnd, HHMM)}`;
};

const hyphen = (short = false) => (short ? '-' : ' - ');

const genFormat = (start, end, list = []) => {
  const formatting = list.find(testFunc(start, end));

  if (formatting) {
    return formatting.onlyStart
      ? `${getThaiDisplayDate(start, formatting.startFormat)}`
      : `${getThaiDisplayDate(start, formatting.startFormat)}${hyphen(
          formatting.adjacent,
        )}${getThaiDisplayDate(end, formatting.endFormat)}`;
  }

  return `${getThaiDisplayDate(start)} - ${getThaiDisplayDate(end)}`;
};

const testFunc = (start, end) => test => {
  if (!test.unit) return true;
  const isSame = start.isSame(end, test.unit);
  if (isSame && test.checkAfter) {
    return end.isAfter(start, test.afterUnit || 'seconds');
  }
  return isSame;
};

export const getTodaysDate = format => {
  return dayjs().format(format || 'YYYY-MM-DD');
};

const testRoute = [
  {
    unit: 'day',
    startFormat: DMYHHMM,
    endFormat: HHMM,
    checkAfter: true,
    afterUnit: 'minute',
    adjacent: true,
  }, // 25 มี.ค. 61 12:00-15:30
  {
    unit: 'day',
    startFormat: DMY,
    endFormat: HHMM,
    checkAfter: false,
    onlyStart: true,
  }, // 25 มี.ค. 61
  { unit: 'month', startFormat: D, endFormat: DMY, adjacent: true }, // 25-28 ก.พ. 61
  { unit: 'year', startFormat: DM, endFormat: DMY }, // 25 ก.พ. - 30 มี.ค. 61
  { startFormat: DMY, endFormat: DMY }, // 25 ธ.ค. 60 - 30 มี.ค. 61
];

export default {
  getThaiDisplayDate,
  isInBetween,
  getDayOfWeekTH,
  getRemainingDays,
  getDiff,
  generateDateRange,
  generateTimeRange,
  getExamDuration,
  getTimeFromNow,
  getCurrentEducationYear,
  getTodaysDate,
};
